<template>
  <div class="admin-root flex flex-col">
    <!-- Main Layout Container -->
    <div class="flex flex-1 min-h-0">
      <!-- Sidebar -->
      <aside class="admin-sidebar w-64 flex-shrink-0 hidden lg:block">
        <div class="p-4">
          <h2 class="font-semibold text-lg mb-4">{{ t('admin.chrome.menuTitle') }}</h2>
          <nav class="space-y-4">
            <div v-for="group in adminNavGroups" :key="group.id" class="space-y-1">
              <div class="admin-nav-group-label px-3 mb-1">{{ t(group.labelKey) }}</div>
              <template v-for="item in group.items" :key="item.name">
                <RouterLink v-if="!item.children" :to="{ name: item.name }" :class="navLinkClass(item)">
                  <VaIcon :name="item.icon" class="text-lg" />
                  <span>{{ t(item.labelKey) }}</span>
                </RouterLink>
                <div v-else>
                  <RouterLink :to="{ name: item.name }" :class="navLinkClass(item)">
                    <VaIcon :name="item.icon" class="text-lg" />
                    <span>{{ t(item.labelKey) }}</span>
                  </RouterLink>
                  <div v-if="isAdminNavActive(route.name, item)" class="pl-4 space-y-1 mt-1">
                    <RouterLink
                      v-for="child in item.children"
                      :key="child.name"
                      :to="{ name: child.name }"
                      :class="navLinkClass(child)"
                    >
                      <VaIcon :name="child.icon" class="text-lg" />
                      <span>{{ t(child.labelKey) }}</span>
                    </RouterLink>
                  </div>
                </div>
              </template>
            </div>
          </nav>
        </div>
      </aside>

      <!-- Mobile Sidebar Overlay -->
      <div
        v-if="!sidebarMinimized"
        class="lg:hidden fixed inset-0 z-30 bg-black bg-opacity-50"
        @click="sidebarMinimized = true"
      ></div>

      <!-- Mobile Sidebar -->
      <aside v-if="!sidebarMinimized" class="admin-sidebar lg:hidden fixed inset-y-0 left-0 w-64 z-40 overflow-y-auto">
        <div class="p-4">
          <h2 class="font-semibold text-lg mb-4">{{ t('admin.chrome.menuTitle') }}</h2>
          <nav class="space-y-4">
            <div v-for="group in adminNavGroups" :key="group.id" class="space-y-1">
              <div class="admin-nav-group-label px-3 mb-1">{{ t(group.labelKey) }}</div>
              <template v-for="item in group.items" :key="item.name">
                <RouterLink
                  v-if="!item.children"
                  :to="{ name: item.name }"
                  :class="navLinkClass(item)"
                  @click="sidebarMinimized = true"
                >
                  <VaIcon :name="item.icon" class="text-lg" />
                  <span>{{ t(item.labelKey) }}</span>
                </RouterLink>
                <div v-else>
                  <RouterLink :to="{ name: item.name }" :class="navLinkClass(item)" @click="sidebarMinimized = true">
                    <VaIcon :name="item.icon" class="text-lg" />
                    <span>{{ t(item.labelKey) }}</span>
                  </RouterLink>
                  <div v-if="isAdminNavActive(route.name, item)" class="pl-4 space-y-1 mt-1">
                    <RouterLink
                      v-for="child in item.children"
                      :key="child.name"
                      :to="{ name: child.name }"
                      :class="navLinkClass(child)"
                      @click="sidebarMinimized = true"
                    >
                      <VaIcon :name="child.icon" class="text-lg" />
                      <span>{{ t(child.labelKey) }}</span>
                    </RouterLink>
                  </div>
                </div>
              </template>
            </div>
          </nav>
        </div>
      </aside>

      <!-- Main Content Area -->
      <div class="flex-1 min-h-0 flex flex-col">
        <header class="admin-shell-header z-50 px-4 lg:px-6 py-3">
          <div class="flex items-center justify-between gap-3 min-w-0">
            <div class="flex items-center gap-2 min-w-0 flex-1">
              <VaButton
                class="lg:hidden border text-[color:var(--admin-text)] flex-shrink-0"
                :style="{ background: 'var(--admin-panel)', borderColor: 'var(--admin-border)' }"
                size="small"
                @click="sidebarMinimized = !sidebarMinimized"
              >
                <VaIcon name="menu" />
              </VaButton>

              <nav class="flex items-center space-x-2 text-sm overflow-x-auto min-w-0">
                <template v-for="(item, index) in breadcrumbItems" :key="index">
                  <RouterLink
                    v-if="item.path"
                    :to="item.path"
                    class="admin-muted hover:opacity-80 transition-colors whitespace-nowrap"
                  >
                    {{ item.title }}
                  </RouterLink>
                  <span v-else class="whitespace-nowrap">
                    {{ item.title }}
                  </span>
                  <VaIcon
                    v-if="index < breadcrumbItems.length - 1"
                    name="chevron_right"
                    class="admin-muted text-xs flex-shrink-0"
                  />
                </template>
              </nav>
            </div>

            <div class="flex items-center gap-3 flex-shrink-0">
              <span class="inline-flex items-center gap-2 min-w-0">
                <span class="inline-block w-2 h-2 rounded-full flex-shrink-0" style="background: var(--admin-accent)" />
                <span class="admin-muted text-xs uppercase tracking-wider truncate max-w-[10rem] sm:max-w-none">
                  {{ t('admin.chrome.sessionLabel') }}
                </span>
              </span>
              <VaButton size="small" preset="secondary" @click="handleExitAdminMode">
                <VaIcon name="logout" class="mr-1" />
                {{ t('admin.chrome.exit') }}
              </VaButton>
            </div>
          </div>
        </header>

        <main class="flex-1 min-h-0 overflow-y-auto">
          <RouterView />
        </main>
      </div>
    </div>

    <!-- Session Warning -->
    <div v-if="showSessionWarning" class="fixed bottom-4 right-4 left-4 lg:left-auto lg:max-w-sm z-50">
      <div class="bg-yellow-900 border border-yellow-600 rounded-lg p-4 shadow-lg">
        <div class="flex items-start space-x-3">
          <VaIcon name="timer" class="text-yellow-400 text-lg mt-0.5 flex-shrink-0" />
          <div class="flex-1 min-w-0">
            <p class="text-yellow-100 text-sm font-medium mb-2">
              {{ t('admin.chrome.sessionWarning', { time: sessionWarningText }) }}
            </p>
            <VaButton
              size="small"
              class="bg-yellow-600 hover:bg-yellow-700 text-white w-full sm:w-auto"
              @click="extendSession"
            >
              {{ t('admin.chrome.extendSession') }}
            </VaButton>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useAuthStore } from '../stores/auth'
import { useRouter, useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useAdminAccess } from '../composables/useAdminAccess'
import { adminNavGroups, isAdminNavActive, type AdminNavItem } from './adminNav'
import '../styles/admin-theme.css'

const { t } = useI18n()
const authStore = useAuthStore()
const router = useRouter()
const route = useRoute()
const {
  exitAdminMode,
  updateActivity,
  extendSession: extendAdminSession,
  stampAdminActivity,
  refreshSessionState,
  logAdminAction,
  sessionWarningText,
  shouldShowWarning,
  enforceSessionOrExit,
} = useAdminAccess()

// Reactive state
const sidebarMinimized = ref(false)
const showSessionWarning = ref(false)
const sessionCheckInterval = ref<number | null>(null)

// Computed properties
const _userEmail = computed(() => authStore.user?.email || '')

const breadcrumbItems = computed(() => {
  const items = []

  // If we're on the main admin page, show only "Администрирование"
  if (route.path === '/admin') {
    items.push({ title: 'Администрирование', path: '/admin' })
  } else {
    // For sub-pages, show only Администрирование > current page (no Dashboard)
    // Add current route breadcrumb if available (it already includes "Администрирование")
    if (route.meta?.breadcrumb && Array.isArray(route.meta.breadcrumb)) {
      items.push(...route.meta.breadcrumb)
    }
  }

  return items
})

const navLinkClass = (item: AdminNavItem) => [
  'admin-nav-link flex items-center gap-3 px-3 py-2 rounded-r text-sm font-medium transition-colors',
  isAdminNavActive(route.name, item) ? 'is-active' : '',
]

// Methods
const handleExitAdminMode = () => {
  logAdminAction('admin_session_exit', {
    method: 'manual_exit',
    route: route.fullPath,
  })

  exitAdminMode()
}

const extendSession = () => {
  extendAdminSession()
  showSessionWarning.value = false
}

const checkSessionTimeout = () => {
  if (!enforceSessionOrExit()) {
    showSessionWarning.value = false
    return
  }

  showSessionWarning.value = shouldShowWarning.value
}

// Lifecycle
onMounted(() => {
  stampAdminActivity()
  checkSessionTimeout()

  logAdminAction('admin_layout_loaded', {
    route: route.fullPath,
  })

  sessionCheckInterval.value = setInterval(checkSessionTimeout, 30000) as unknown as number

  // Update activity on route change (but not too frequently)
  const unsubscribe = router.afterEach((to) => {
    if (to.path.startsWith('/admin')) {
      refreshSessionState()
      updateActivity()
    }
  })

  // Store unsubscribe for cleanup
  const cleanup = () => {
    if (sessionCheckInterval.value) {
      clearInterval(sessionCheckInterval.value)
    }
    unsubscribe()
  }

  // Set up cleanup
  onUnmounted(cleanup)
})
</script>
